import React, { useEffect } from 'react';
import Helmet from 'react-helmet';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import LinearProgress from '@material-ui/core/LinearProgress';

import { selectUser, selectOrganization } from 'entities/user/selectors';
import OrganizationMenu from 'retired/OrganizationMenu';
import { useInjectEpics } from 'utils/injectEpics';
import { Privileges } from 'utils/privileges';
import type { State } from 'reducers';
import { useInjectReducer } from 'utils/injectReducer';

import { key } from './constants';
import * as Actions from './actions';
import epics from './epics';
import { DeedFeeRates } from './deedFeeRates';
import { OrganizationDocuments } from './OrganizationDocuments';
import NonprofitVerificationStatus from './NonprofitVerificationStatus';
import PresenceFormSection from './PresenceFormSection';
import PointOfContact from './PointOfContact';
import Brand from './Brand';
import reducer from './reducer';
import { selectLoading } from './selectors';

const OrganizationPageWrapper = ({ children }: { children: React.ReactNode }) => (
  <>
    <Helmet title="Organization Settings" />
    <div style={{ marginBottom: 60 }}>
      <OrganizationMenu />
    </div>
    {children}
  </>
);

const OrganizationPage = () => {
  const loading = useSelector(selectLoading);
  const organization = useSelector((state: State) => selectOrganization(state, true));
  const user = useSelector(selectUser);

  const dispatch = useDispatch();
  const history = useHistory();

  useEffect(() => {
    if (user && !user.hasPrivilege(Privileges.ORGANIZATION_MANAGE_SETTINGS)) {
      if (user.hasPrivilege(Privileges.ORGANIZATION_VIEW_ORGANIZERS)) {
        history.replace('/organizers');
      } else {
        history.replace('/');
      }
    }
  });

  useInjectEpics({ key, epics });
  useInjectReducer({ key, reducer });
  useEffect(() => {
    dispatch(Actions.initAction());
  }, []);

  if (!organization) {
    return <LinearProgress />;
  }

  const isCompany = organization.isCompany();
  const isAdmin = user?.type === 'Admin';

  if (loading) {
    return (
      <OrganizationPageWrapper>
        <LinearProgress />
      </OrganizationPageWrapper>
    );
  }

  return (
    <OrganizationPageWrapper>
      <Brand />

      <PresenceFormSection />

      <PointOfContact />

      {isCompany && isAdmin && <DeedFeeRates />}
      {organization.isNonprofit() && isAdmin && (
        <>
          <OrganizationDocuments organization={organization} />
          <NonprofitVerificationStatus />
        </>
      )}
    </OrganizationPageWrapper>
  );
};

export default OrganizationPage;
